/**
 * アンケート設問の「提示タイミング（cue）」選択ロジック。
 *
 * 視聴プレイヤーが `currentTime`（再生位置・秒）と回答済み集合をもとに、
 * 「今どの設問を提示すべきか」を決める純粋関数群。副作用を持たず、
 * React state に依存しないため単体テストしやすい形で切り出している。
 *
 * 提示ルール（スペック #232 / #230 Pattern D 準拠）:
 * - cueTime != null の設問: `currentTime >= cueTime` に到達したら提示対象。
 * - cueTime == null の設問: 動画末尾（VIDEO_END）で提示対象（シーケンス提示）。
 * - 提示順は cueTime 昇順 → order 昇順。null-cue（末尾）は最後。
 * - required（必須）: cue 到達で強制的に一時停止＋シート展開。
 * - 任意: cue 到達で「回答はこちら↓」オプトインタブ（動画は継続）。
 * - 回答済み questionId は再提示しない（視聴セッション内で保持）。
 */

import type { LeadFieldKind, SurveyQuestion } from '@/types/interactiveVideo'
import { EMAIL_PATTERN } from '@/validate/constants'

/**
 * リード（PII）項目の入力欄プレースホルダ（項目型ごと）。固定型（メール/氏名/会社/電話/部署）＋
 * 自由ラベル PII テキスト（TEXT）に対応する（ADR-0012・#264）。ゲート（SurveyGate）と動画シート
 * （SurveySheet）で共有する。
 */
export const LEAD_PLACEHOLDER: Record<LeadFieldKind, string> = {
  EMAIL: 'メールアドレス',
  NAME: 'お名前',
  COMPANY: '会社名',
  PHONE: '電話番号',
  DEPARTMENT: '部署',
  TEXT: 'ご入力ください',
}

/**
 * リード（PII）値が有効かを判定する。EMAIL 型のみ形式検証を継承し、他の固定型/TEXT は非空なら有効。
 * これはクライアント側の送信可否（UX ミラー）で、権威的な検証はサーバ（submitSurvey）にある。
 */
export function isLeadValueValid(
  leadFieldKind: LeadFieldKind,
  value: string,
): boolean {
  const trimmed = value.trim()
  if (leadFieldKind === 'EMAIL') return EMAIL_PATTERN.test(trimmed)
  return trimmed.length > 0
}

export interface SurveyCueInput {
  questions: SurveyQuestion[]
  /** 現在の再生位置（秒） */
  currentTime: number
  /** 回答済みの questionId 集合（視聴セッション単位） */
  answeredIds: ReadonlySet<string>
  /** 動画が末尾まで再生されたか（null-cue 設問の提示判定に使う） */
  videoEnded: boolean
  /**
   * 動画の長さ（秒）。分かっていれば `cueTime` が動画長を超える設問を末尾提示へ
   * フォールバックさせる（ADR-0015）。テンプレを長さの違う動画で共有したときに
   * 「cue に到達せず永久に出ない」取りこぼしを防ぐ。未知なら省略する。
   */
  videoDuration?: number | null
}

/**
 * その設問が実質「末尾提示」になるか。
 * `cueTime=null`（末尾指定）に加え、動画長を超える `cueTime` も末尾へ倒す（ADR-0015）。
 */
export function isEndCued(
  question: SurveyQuestion,
  videoDuration: number | null | undefined,
): boolean {
  if (question.cueTime == null) return true
  return (
    videoDuration != null &&
    Number.isFinite(videoDuration) &&
    question.cueTime > videoDuration
  )
}

/**
 * 提示順に設問を並べ替える（cueTime 昇順 → order 昇順、null-cue は末尾）。
 * 元配列は変更しない。
 */
export function sortSurveyQuestions(questions: SurveyQuestion[]): SurveyQuestion[] {
  return [...questions].sort((a, b) => {
    // null-cue（末尾提示）は常に後ろ
    if (a.cueTime == null && b.cueTime == null) return a.order - b.order
    if (a.cueTime == null) return 1
    if (b.cueTime == null) return -1
    if (a.cueTime !== b.cueTime) return a.cueTime - b.cueTime
    return a.order - b.order
  })
}

/**
 * ある設問が「今提示すべき状態（due）」かを判定する。
 * - 回答済みは常に false。
 * - 末尾扱い（cueTime null、または動画長を超える cueTime）: 動画が末尾まで再生されたら true。
 * - それ以外: 再生位置が cueTime 以上なら true。
 */
function isQuestionDue(question: SurveyQuestion, input: SurveyCueInput): boolean {
  if (input.answeredIds.has(question.id)) return false
  if (isEndCued(question, input.videoDuration)) return input.videoEnded
  return input.currentTime >= question.cueTime!
}

/**
 * 現在提示対象（due）かつ未回答の設問を、提示順に並べて返す。
 */
export function getDueQuestions(input: SurveyCueInput): SurveyQuestion[] {
  return sortSurveyQuestions(input.questions).filter((q) => isQuestionDue(q, input))
}

/**
 * 強制提示（必須・強制ポーズ）すべき設問を1つ返す。
 * due かつ未回答の required 設問のうち、提示順で最初のもの。無ければ null。
 */
export function pickForcedQuestion(input: SurveyCueInput): SurveyQuestion | null {
  return getDueQuestions(input).find((q) => q.required) ?? null
}

/**
 * オプトインタブとして提示する任意設問を1つ返す。
 * due かつ未回答の任意（required=false）設問のうち、提示順（cueTime 昇順→order）で
 * 最初のもの。無ければ null。
 *
 * 最初（＝最も早い cue）を選ぶのは、必須の強制提示（pickForcedQuestion）と提示順を
 * 揃え、著者が意図した順に1問ずつ案内するため。後続の任意設問の cue に到達しても
 * 先行の未回答任意を飛ばさない（＝取りこぼしを防ぐ）。
 */
export function pickOptInQuestion(input: SurveyCueInput): SurveyQuestion | null {
  return getDueQuestions(input).find((q) => !q.required) ?? null
}

/**
 * SURVEYノード（POPUP 全画面ゲート・#263）で提示する設問群。
 * 同一視聴セッションで回答済みの設問は除外して再提示しない。提示順は order 昇順
 * （SURVEYノードは cueTime を持たないため sortSurveyQuestions は order だけで整列する）。
 */
export function getGateQuestions(
  questions: SurveyQuestion[],
  answeredIds: ReadonlySet<string>,
): SurveyQuestion[] {
  return sortSurveyQuestions(questions).filter((question) => !answeredIds.has(question.id))
}

/**
 * SURVEYノードのゲートを通過（外向き遷移）してよいかを判定する純粋関数。
 * 提示中の設問のうち必須設問がすべて回答済み（answeredIds に含まれる）なら true。
 * 未回答の必須設問が1つでもあれば false（＝遷移ブロック）。
 *
 * これはクライアント側の UX ミラーで、権威的な「答えた人だけ次へ」の判定は
 * サーバの送信ゲート（saveSurveyResponse）にある（#263）。
 */
export function canLeaveSurveyGate(
  questions: SurveyQuestion[],
  answeredIds: ReadonlySet<string>,
): boolean {
  return questions.every((question) => !question.required || answeredIds.has(question.id))
}

/**
 * このノードのアンケート進捗（回答済み数 / 総設問数）を返す。
 * 進捗「n/N」表示に使う。
 */
export function surveyProgress(
  questions: SurveyQuestion[],
  answeredIds: ReadonlySet<string>,
): { answered: number; total: number } {
  const total = questions.length
  const answered = questions.filter((q) => answeredIds.has(q.id)).length
  return { answered, total }
}
